// @file Popup that appears when user clicks on a post on a map
import { GeoMap, LatLng, LatLngLiteral, OverlayView, Point } from '@@/bits/map'

function generateContent(content: Node | string | undefined): Node {
  if (typeof content === 'string') {
    const span = document.createElement('span')
    span.innerText = content
    return span
  }
  if (content instanceof Node) {
    return content
  }
  return document.createElement('div')
}

interface ConstructorParams {
  position?: LatLng | LatLngLiteral
  content?: Node | string
  map: GeoMap | null
}

function divOffset(originalDiv: HTMLDivElement): { x: number; y: number } {
  const offset = { x: 0, y: 0 }
  let div: HTMLElement = originalDiv
  do {
    offset.x += div.offsetLeft
    offset.y += div.offsetTop
    div = div.offsetParent as HTMLElement
  } while (div)
  return offset
}

export default class CustomPopup extends OverlayView {
  private div?: HTMLDivElement
  private position!: LatLng
  private content!: Node
  private map: GeoMap | null
  private _isOpened = false
  public constructor({ position, content, map = null }: ConstructorParams) {
    super()
    this.setPosition(position)
    this.content = generateContent(content)
    this.setMap(map)
    this.map = map
  }

  public get isOpened(): boolean {
    return this._isOpened
  }

  public setPosition(position: LatLng | LatLngLiteral | undefined): void {
    if (position) {
      this.position = position instanceof LatLng ? position : new LatLng(position.lat, position.lng)
    }
  }

  public setContent(content: Node | string | undefined): void {
    this.content = generateContent(content)
    this.draw()
  }

  public draw(): void {
    if (this.div) {
      this.div.parentElement && this.div.parentElement.removeChild(this.div)
      // This zero-height div is positioned at the bottom of the bubble.
      const bubbleAnchor = document.createElement('div')
      bubbleAnchor.classList.add('popup-bubble-anchor')
      const bubble = document.createElement('div')
      bubble.classList.add('popup-bubble')
      bubble.appendChild(this.content)
      bubbleAnchor.appendChild(bubble)

      // This zero-height div is positioned at the bottom of the tip.
      this.div = document.createElement('div')
      this.div.classList.add('popup-container')
      this.div.appendChild(bubbleAnchor)

      // Optionally stop clicks, etc., from bubbling up to the map.
      // @ts-ignore - type definition library missing the static method
      OverlayView.preventMapHitsAndGesturesFrom(this.div)
      if (!this || !this.getPanes() || !this.getProjection()) {
        return
      }
      this.getPanes()?.floatPane && this.getPanes()?.floatPane.appendChild(this.div)
      const divPosition = this.getProjection().fromLatLngToDivPixel(this.position)
      const display =
        Math.abs(divPosition.x) < Math.max(window.screen.availWidth, 4000) &&
        Math.abs(divPosition.y) < Math.max(window.screen.availHeight, 4000)
          ? 'block'
          : 'none'
      if (display === 'block') {
        this.div.style.left = divPosition.x + 'px'
        this.div.style.top = divPosition.y + 'px'
      }
      if (this.div.style.display !== display) {
        this.div.style.display = display
      }
    } else {
      // this.div.style.display = 'none'
    }
  }

  public open(map: GeoMap): void {
    this._isOpened = true
    this.setMap(map)
  }

  public close(): void {
    this._isOpened = false
    this.setMap(null)
  }

  public onAdd(): void {
    this.div = document.createElement('div')

    // Optionally stop clicks, etc., from bubbling up to the map.
    // @ts-ignore - type definition library missing the static method
    OverlayView.preventMapHitsAndGesturesFrom(this.div)
  }

  public onRemove(): void {
    if (this.div && this.div.parentElement) {
      this.div.parentElement.removeChild(this.div)
    }
  }

  public fromPixelToLatLng({ x, y }: { x: number; y: number }): LatLng | null {
    if (!this.map) {
      return null
    }
    const offset = divOffset((this.map as GeoMap).getDiv() as HTMLDivElement)
    return this.getProjection().fromContainerPixelToLatLng(new Point(x - offset.x, y - offset.y))
  }

  public fromLatLngToPixel(latLng: LatLng): { x: number; y: number } | null {
    if (!this.map) {
      return null
    }
    const offset = divOffset((this.map as GeoMap).getDiv() as HTMLDivElement)
    const containerPixel = this.getProjection().fromLatLngToContainerPixel(latLng)
    return { x: containerPixel.x + offset.x, y: containerPixel.y + offset.y }
  }

  public fromLatLngLiteralToPixel(latLngLiteral: LatLngLiteral): { x: number; y: number } | null {
    if (!this.map) {
      return null
    }
    const offset = divOffset((this.map as GeoMap).getDiv() as HTMLDivElement)
    const containerPixel = this.getProjection().fromLatLngToContainerPixel(new LatLng(latLngLiteral))
    return { x: containerPixel.x + offset.x, y: containerPixel.y + offset.y }
  }
}
